import { useMemo } from 'react'
import {
  type CompanyData,
  type CompanyId,
  DEFAULT_TIMEZONE,
  isNotEmpty,
  messageFromError,
  type QuestionData,
  type RoleData,
  type ReportKpiFilter,
  type ReportKpiType,
  splitTeamMembers,
  type TeamData,
  type TeamMemberData,
  type UserData,
  type UserId,
} from '@terros/common'
import { useProfile } from '../user'
import { useTeams } from '../team'
import { useDecoratedReportKpiV2, type DecoratedReportKpiInput } from '../report'
import { type GetHookResponse } from '../hooks'
import { type QuestionResponseCounts, type QuestionTrendBucket } from './reportTypes'
import { getScopeFilter, type QuestionReportScope } from './reportScope'
import {
  getQuestionAverageScore,
  getCalendarYearRange,
  getQuestionReportKpis,
  getQuestionReportYears,
  getReportPeriodFromQuestion,
  resolveQuestionReportYear,
  toQuestionCounts,
  toTrendBuckets,
  withReportRange,
  type QuestionReportKpis,
  type QuestionReportRange,
} from './reportKpis'

type Props = {
  question: QuestionData
  companyId: CompanyId
  company?: CompanyData
  scope: QuestionReportScope
  year?: number
  selectedBucket?: QuestionTrendBucket
  enabled: boolean
}

export type QuestionReportData = {
  teamsState: GetHookResponse<TeamData[]>
  teams: TeamData[]
  kpis: QuestionReportKpis
  activeFilter: ReportKpiFilter
  responseCounts: QuestionResponseCounts
  hasReportableResponses: boolean
  averageScore?: number
  trendBuckets: QuestionTrendBucket[]
  reportRange: QuestionReportRange
  reportYear: number
  isPrimaryResultsLoading: boolean
  isTrendLoading: boolean
  primaryError?: string
  trendError?: string
}

export const useQuestionReportData = ({
  question,
  companyId,
  scope,
  year,
  selectedBucket,
  enabled,
}: Props): QuestionReportData => {
  const teamsState = useTeams({ companyId, enabled })
  const { user } = useProfile()
  const timezone = user?.timeZone ?? DEFAULT_TIMEZONE
  const reportYears = useMemo(() => getQuestionReportYears(question, timezone), [question, timezone])
  const reportYear = resolveQuestionReportYear(year, reportYears, timezone)
  const reportRange = useMemo(() => getCalendarYearRange(reportYear, timezone), [reportYear, timezone])
  const teams = useMemo(() => teamsState.data ?? [], [teamsState.data])
  const kpis = useMemo(() => getQuestionReportKpis(question), [question])
  const period = getReportPeriodFromQuestion(question)
  const scopeFilter = useMemo(() => getScopeFilter(scope), [scope])
  const activeFilter = useMemo(() => {
    if (!selectedBucket) return withReportRange(scopeFilter, reportRange)
    return {
      ...scopeFilter,
      startDate: selectedBucket.startDate,
      endDate: selectedBucket.endDate,
    }
  }, [scopeFilter, selectedBucket, reportRange])
  const trendFilter = useMemo(() => withReportRange(scopeFilter, reportRange), [scopeFilter, reportRange])

  const summaryState = useDecoratedReportKpiV2(
    { kpis: kpis.all, filter: activeFilter, grouping: { type: 'company' } },
    { enabled }
  )
  const trendState = useDecoratedReportKpiV2(
    {
      kpis: kpis.all,
      filter: trendFilter,
      grouping: { type: 'time', period },
    },
    { enabled }
  )

  const responseCounts = useMemo(() => toQuestionCounts(summaryState.data, kpis), [summaryState.data, kpis])
  const averageScore = useMemo(
    () => getQuestionAverageScore(responseCounts.choiceCounts, kpis.choiceIndexes),
    [responseCounts.choiceCounts, kpis.choiceIndexes]
  )
  const trendBuckets = useMemo(
    () => toTrendBuckets(trendState.data, kpis, period, reportRange, timezone),
    [trendState.data, kpis, period, reportRange, timezone]
  )

  return {
    teamsState,
    teams,
    kpis,
    activeFilter,
    responseCounts,
    hasReportableResponses: isNotEmpty(responseCounts.answerCount),
    averageScore,
    trendBuckets,
    reportRange,
    reportYear,
    isPrimaryResultsLoading: summaryState.isLoading,
    isTrendLoading: trendState.isLoading,
    primaryError: messageFromError(summaryState.error),
    trendError: messageFromError(trendState.error),
  }
}

export const getQuestionManagerReportInput = ({
  kpis,
  filter,
  managerIds,
}: {
  kpis: ReportKpiType[]
  filter: ReportKpiFilter
  managerIds: UserId[]
}): DecoratedReportKpiInput => ({
  kpis,
  filter: { ...filter, managers: managerIds },
  grouping: { type: 'manager' },
  showArchived: true,
})

type DirectManagerMember = Pick<TeamMemberData, 'roleIds'> & {
  user: Pick<UserData, 'userId'>
}

export const getDirectManagerIds = (
  members: DirectManagerMember[] | undefined,
  roles: Pick<RoleData, 'roleId' | 'roleType'>[] | undefined
): UserId[] => {
  if (!members || !roles) return []
  return splitTeamMembers(members, roles).managers.map((member) => member.user.userId)
}
